<template>
  <div class="event-form-container">
    <v-card flat>
      <v-card-text class="pa-6">
        <v-form ref="eventoForm" v-model="isValid">
          <v-row>
            <!-- Sección: Información Principal -->
            <v-col cols="12">
              <div class="d-flex align-center mb-4">
                <v-icon color="primary" class="mr-2">mdi-information-outline</v-icon>
                <span class="text-h6 font-weight-medium">Información del Evento</span>
              </div>
            </v-col>

            <v-col cols="12" md="8">
              <v-text-field v-model="eventoData.nm" label="Nombre del Evento" placeholder="Ej. Cena de Navidad" outlined
                dense prepend-inner-icon="mdi-format-title" class="mb-2"
                :rules="[v => !!v || 'El nombre es requerido']"></v-text-field>

              <v-textarea v-model="eventoData.ds" label="Descripción"
                placeholder="Describe los detalles importantes del evento..." outlined dense rows="5"
                prepend-inner-icon="mdi-text-subject" counter maxlength="500"></v-textarea>
            </v-col>

            <v-col cols="12" md="4" class="text-center">
              <div class="text-caption mb-2 grey--text text--darken-1">Imagen destacada</div>
              <v-hover v-slot="{ hover }">
                <v-card outlined class="mx-auto image-preview-card" :class="{ 'elevation-4': hover }" max-width="200"
                  height="200">
                  <v-img :src="avatarUrl" height="100%" width="100%" class="grey lighten-4">
                    <template v-slot:placeholder>
                      <v-row class="fill-height ma-0" align="center" justify="center">
                        <v-icon large color="grey lighten-1">mdi-image-plus</v-icon>
                      </v-row>
                    </template>
                    <v-overlay absolute :value="hover" color="primary" opacity="0.5">
                      <v-btn fab small color="white" @click="dialog = true; index++;">
                        <v-icon color="primary">mdi-camera</v-icon>
                      </v-btn>
                    </v-overlay>
                  </v-img>
                </v-card>
              </v-hover>
              <v-btn text color="primary" small class="mt-2 text-none" @click="dialog = true; index++;">
                {{ avatarUrl.includes('placeholder') ? 'Cargar Imagen' : 'Cambiar Imagen' }}
              </v-btn>
            </v-col>

            <!-- Sección: Configuración y Programación -->
            <v-col cols="12" class="mt-4">
              <div class="d-flex align-center mb-4">
                <v-icon color="primary" class="mr-2">mdi-calendar-clock</v-icon>
                <span class="text-h6 font-weight-medium">Programación y Categoría</span>
              </div>
            </v-col>

            <v-col cols="12" md="6">
              <v-text-field v-model="agrupador" label="Agrupador / Categoría"
                placeholder="Ej. Restaurante, Golf, Social" outlined dense prepend-inner-icon="mdi-tag-outline"
                :rules="[v => !!v || 'El agrupador es requerido']"></v-text-field>
            </v-col>

            <v-col cols="12" md="6">
              <v-select v-model="eventoData.tr" :items="tiposRecurrencia" label="Tipo de Frecuencia" outlined dense
                prepend-inner-icon="mdi-repeat" :rules="[v => !!v || 'Seleccione un tipo']"></v-select>
            </v-col>

            <v-col cols="12" v-if="eventoData.tr === 'F'">
              <v-menu v-model="dateMenu" :close-on-content-click="false" transition="scale-transition" offset-y
                min-width="auto">
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field v-model="fecha" label="Fecha del Evento" prepend-inner-icon="mdi-calendar-range"
                    readonly outlined dense v-bind="attrs" v-on="on"
                    :rules="[v => !!v || 'La fecha es requerida']"></v-text-field>
                </template>
                <v-date-picker v-model="fecha" @input="dateMenu = false" no-title scrollable
                  color="primary"></v-date-picker>
              </v-menu>
            </v-col>

            <v-col cols="12" v-if="eventoData.tr === 'R'">
              <div class="text-body-2 mb-2 ml-1">Seleccione los días de la semana:</div>
              <v-chip-group v-model="eventoData.vr" multiple active-class="primary " column>
                <v-chip v-for="dia in diasSemana" :key="dia.id" :value="dia.id" outlined filter small>
                  {{ dia.nombre }}
                </v-chip>
              </v-chip-group>
              <div v-if="eventoData.vr.length === 0" class="text-caption error--text ml-1 mt-1">
                Debe seleccionar al menos un día
              </div>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="pa-4 sticky-actions">
        <v-spacer></v-spacer>
        <v-btn text @click="$emit('toggle', false)" class="text-none">
          Cancelar
        </v-btn>
        <v-btn color="primary" @click="save"
          :disabled="!isValid || (eventoData.tr === 'R' && eventoData.vr.length === 0)" elevation="0"
          class="px-8 text-none">
          Guardar Evento
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- Diálogo de Recorte de Imagen -->
    <v-dialog :key="index" v-model="dialog" persistent max-width="500" scrollable>
      <v-card>
        <v-card-title class="text-h6 primary white--text">
          Ajustar imagen
          <v-spacer></v-spacer>
          <v-btn icon dark @click="dialog = false; imgLoading = false;">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pa-4 pt-6">
          <div class="d-flex justify-center mb-6">
            <v-btn color="primary" @click="$refs.FileInput.click()" rounded depressed>
              <v-icon left>mdi-image-search</v-icon> Seleccionar Archivo
            </v-btn>
            <input ref="FileInput" type="file" style="display: none;" accept="image/png, image/jpeg"
              @change="onFileSelect" />
          </div>

          <div v-show="imgLoading" class="cropper-wrapper elevation-2 rounded mb-4">
            <vue-cropper preview=".preview" ref="cropper" :src="selectedFile"></vue-cropper>
          </div>

          <div v-if="imgLoading" class="text-center mt-4">
            <v-btn-toggle dense rounded background-color="grey lighten-4">
              <v-btn @click="rotate(-90)" small>
                <v-icon>mdi-rotate-left</v-icon>
              </v-btn>
              <v-btn @click="zoom(-0.2)" small>
                <v-icon>mdi-magnify-minus</v-icon>
              </v-btn>
              <v-btn @click="zoom(0.2)" small>
                <v-icon>mdi-magnify-plus</v-icon>
              </v-btn>
              <v-btn @click="rotate(90)" small>
                <v-icon>mdi-rotate-right</v-icon>
              </v-btn>
            </v-btn-toggle>
          </div>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn text @click="dialog = false; imgLoading = false;" class="text-none">
            Cerrar
          </v-btn>
          <v-btn v-show="imgLoading" :loading="loaderImage" color="primary" class="px-6 text-none" @click="saveImage()">
            Confirmar y Guardar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import VueCropper from "vue-cropperjs";
import { getStorage, ref, listAll, getDownloadURL } from "firebase/storage";
import "cropperjs/dist/cropper.css";

export default {
  name: "EventosForm",
  components: { VueCropper },
  props: {
    edit: {
      type: Boolean,
      default: false
    },
    dataEdit: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      index: 0,
      dialog: false,
      blobData: null,
      mime_type: "",
      cropedImage: "",
      autoCrop: false,
      selectedFile: "/assets/img/iconUser.png",
      imgLoading: false,
      loaderImage: false,
      avatarUrl: require("@/assets/img/image-placeholder.png"),
      isValid: true,
      dateMenu: false,
      agrupador: "",
      fecha: "",
      eventoData: {
        id: null,
        Multimedia: null,
        ag: [],
        nm: "",
        ds: "",
        at: true,
        tr: "F",
        vr: [],
      },
      tiposRecurrencia: [
        { text: "Fecha Fija", value: "F" },
        { text: "Se repite semanalmente", value: "R" }
      ],
      diasSemana: [
        { id: 0, nombre: "Dom" },
        { id: 1, nombre: "Lun" },
        { id: 2, nombre: "Mar" },
        { id: 3, nombre: "Mié" },
        { id: 4, nombre: "Jue" },
        { id: 5, nombre: "Vie" },
        { id: 6, nombre: "Sáb" }
      ]
    };
  },
  mounted() {
    if (this.edit && this.dataEdit) {
      this.eventoData = { ...this.dataEdit };
      this.eventoData.id = this.dataEdit.Id;
      this.agrupador = this.dataEdit.ag[0];
      this.getImages(this.dataEdit.Id);
      if (this.dataEdit.tr === "F") {
        this.fecha = this.dataEdit.vr[0] || "";
        this.eventoData.vr = [];
      } else {
        // Ensure vr is an array of integers for the chips
        this.eventoData.vr = Array.isArray(this.dataEdit.vr)
          ? this.dataEdit.vr.map(v => parseInt(v))
          : [];
      }
    }
  },
  methods: {
    async getImages(idEvento) {
      try {
        const url =
          this.$store.getters.getFirebaseApp.tenant +
          "/MKT_Evento/" +
          idEvento +
          "/";

        let app = this.$store.getters.getFirebaseApp;

        const storage = getStorage(
          app.mainApp,
          "pargolfsolutions-e4659.appspot.com"
        );
        const listRef = ref(storage, url);

        const res = await listAll(listRef);
        const tasks = res.items.map((item) => getDownloadURL(item));

        const urls = await Promise.all(tasks);

        this.imageArray = urls.map((url, idx) => ({
          img: url,
          name: res.items[idx].name,
        }));

        if (this.imageArray.length > 0) {
          this.avatarUrl = this.imageArray[0].img;
        }
      } catch (error) {
        console.error("Error al cargar imágenes:", error);
      }
    },
    async saveImage() {
      this.loaderImage = true;
      try {
        const canvas = this.$refs.cropper.getCroppedCanvas();
        if (canvas) {
          this.cropedImage = canvas.toDataURL();
          canvas.toBlob((blob) => {
            this.blobData = blob;
            this.avatarUrl = this.cropedImage;
            this.eventoData.Multimedia = blob;
            this.loaderImage = false;
            this.dialog = false;
          }, this.mime_type);
        }
      } catch (error) {
        console.error("Error al guardar imagen recortada:", error);
        this.loaderImage = false;
      }
    },
    onFileSelect(e) {
      const file = e.target.files[0];
      if (!file) return;

      this.mime_type = file.type;

      if (typeof FileReader === "function") {
        const reader = new FileReader();
        reader.onload = (event) => {
          this.selectedFile = event.target.result;
          this.imgLoading = true;
          this.$refs.cropper.replace(this.selectedFile);
        };
        reader.readAsDataURL(file);
      } else {
        alert("Sorry, FileReader API not supported");
      }
    },
    zoom(percent) {
      this.$refs.cropper.relativeZoom(percent);
    },
    rotate(deg) {
      this.$refs.cropper.rotate(deg);
    },
    save() {
      if (this.$refs.eventoForm.validate()) {
        const mode = this.edit ? "EDIT" : "CREATE";
        this.eventoData.ag = [this.agrupador];

        if (this.eventoData.tr === "F") {
          this.eventoData.vr = [this.fecha];
        } else {
          // Filtro para asegurar solo numeros validos
          this.eventoData.vr = this.eventoData.vr
            .map(v => parseInt(v))
            .filter(v => !isNaN(v) && v >= 0 && v <= 6);
          // Ordenar dias de la semana
          this.eventoData.vr.sort((a, b) => a - b);
        }

        if (mode === "CREATE") {
          this.eventoData.id = null;
        }
        this.$emit("successData", { ...this.eventoData }, mode);
      }
    }
  }
};
</script>

<style scoped>
.event-form-container {
  background-color: #fcfcfc;
}

.image-preview-card {
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.3s ease;
  border: 2px dashed #e0e0e0 !important;
}

.image-preview-card:hover {
  border-color: var(--v-primary-base) !important;
}

.sticky-actions {
  position: sticky;
  bottom: 0px;
  background-color: white !important;
  z-index: 10;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}

.cropper-wrapper {
  max-height: 350px;

}

/* Custom transitions for chip group */
.v-chip-group .v-chip {
  transition: transform 0.2s ease;
}

.v-chip-group .v-chip--active {
  transform: scale(1.05);
}
</style>
